import { gql } from "lib/apolloClient";
import {
  attachmentFragment,
  studentFragment,
  userFragment,
} from "modules/CommonFragments";

export const likefragment = {
  like: gql`
    fragment likeItem on Like {
      id
      user {
        id
        firstName
        lastName
        middleName
        type
        ... on Student {
          pronouns
          preferredName
          suffix
        }
      }
    }
  `,
};

export const postSeenFragment = {
  postSeen: gql`
    fragment postSeenItem on Seen {
      id
      user {
        id
      }
    }
  `,
};

export const messageCommentOfPostFragment = {
  message: gql`
    fragment messageItem on Message {
      id
      label
      itemType
      type
      isDeleted
      isCommentRead
      isRead
      item {
        id
        ... on AttachmentSet {
          id
          attachments {
            ...attachmentItem
          }
        }
      }
      createdAt
      createdBy {
        id
        firstName
        middleName
        lastName
        middleName
        ... on FamilyMember {
          preferredName
          prefix
          suffix
          pronouns
        }
        ... on Staff {
          preferredName
          prefix
          suffix
          pronouns
        }
        ... on Student {
          pronouns
          preferredName
          suffix
        }
        type
        email
        profileImage
      }
    }
    ${attachmentFragment.attachment}
  `,
};

export const routineLikesAndComments = {
  likesAndComments: gql`
    fragment routineLikesAndComments on StudentTemplateResponse {
      id
      comments {
        totalCount
        unreadCountV2
        edges: messageEdges {
          node {
            ...messageItem
          }
        }
      }
      likes {
        totalCount
        edges {
          node {
            ...likeItem
          }
        }
      }
    }
    ${messageCommentOfPostFragment.message}
    ${likefragment.like}
  `,
};

export const routinePostFragment = {
  routinePostFeed: gql`
    fragment postItem on Post {
      id
      title

      createdBy {
        ...userItem
      }
      createdAt
      publishedAt
      seen {
        totalCount
        edges {
          node {
            ...postSeenItem
          }
        }
      }
      state

      course {
        id
        title
      }

      students {
        totalCount
        edges {
          node {
            ...studentItem
          }
        }
      }

      isPrivate
      isHiddenFromStudents
      isStudentIndividuallyRated
      postTemplate {
        id
        fields {
          id
          label
          placeholder
          type
          metadata
          dataSets {
            id
            title
            type
            startDate
            endDate
            values {
              value
              date
              displaySequence
              id
              item {
                id
                itemName
              }
            }
          }
        }
      }
      items {
        item {
          ... on StudentTemplateResponse {
            id
            metadata

            comments {
              totalCount
              unreadCount
              unreadCountV2
              edges: messageEdges(first: 1) {
                node {
                  ...messageItem
                }
              }
            }
            likes {
              totalCount
              edges {
                node {
                  ...likeItem
                }
              }
            }
            individuallyRated
            routineStudent: student {
              ...studentItem
            }
            fields {
              id
              value
              uid
              resolvedMinimalTree {
                id
                type
                ... on ResolvedFieldAttachmentGroup {
                  attachmentGroups {
                    id
                    displaySequence
                    metadata
                    attachments {
                      id
                      type
                      url
                      mimeType
                      displaySequence
                      streamUrl
                      thumbUrl
                    }
                  }
                }
              }
            }
          }
        }
        itemType
      }
    }
    ${studentFragment.basicStudentDetails}
    ${attachmentFragment.attachment}
    ${userFragment.basicUserDetails}
    ${likefragment.like}
    ${postSeenFragment.postSeen}
    ${messageCommentOfPostFragment.message}
  `,
  routinePostDetail: gql`
    fragment postItem on Post {
      id
      title
      attachments {
        ...attachmentItem
        displaySequence
      }
      createdBy {
        ...userItem
      }
      createdAt
      publishedAt
      isDeleted
      comments {
        totalCount
        unreadCount
        edges: messageEdges(first: 1) {
          node {
            ...messageItem
          }
        }
      }
      likes {
        totalCount
        edges {
          node {
            ...likeItem
          }
        }
      }
      seen {
        totalCount
        edges {
          node {
            ...postSeenItem
          }
        }
      }
      state

      course {
        id
        title
      }

      students {
        totalCount
        edges {
          node {
            ...studentItem
          }
        }
      }
      postTemplate {
        id
        fields {
          id
          label
          placeholder
          type
          metadata
          dataSets {
            id
            title
            type
            startDate
            endDate
            values {
              value
              date
              displaySequence
              id
              item {
                id
                itemName
              }
            }
          }
        }
      }
      isPrivate
      isHiddenFromStudents
      isStudentIndividuallyRated
      items {
        item {
          ... on StudentTemplateResponse {
            id
            metadata
            individuallyRated
            routineStudent: student {
              ...studentItem
            }
            fields {
              id
              value
              uid
              resolvedMinimalTree {
                id
                type
                ... on ResolvedFieldAttachmentGroup {
                  attachmentGroups {
                    id
                    displaySequence
                    metadata
                    attachments {
                      ...attachmentItem
                    }
                  }
                }
              }
            }
            ...routineLikesAndComments
          }
        }
        itemType
      }
    }
    ${studentFragment.basicStudentDetails}
    ${attachmentFragment.attachment}
    ${userFragment.basicUserDetails}
    ${likefragment.like}
    ${postSeenFragment.postSeen}
    ${messageCommentOfPostFragment.message}
    ${routineLikesAndComments.likesAndComments}
  `,
  routinePost: gql`
    fragment postItem on Post {
      id
      title
      attachments {
        ...attachmentItem
        displaySequence
      }
      createdBy {
        ...userItem
      }
      createdAt
      publishedAt
      comments {
        totalCount
        unreadCount
      }
      likes {
        totalCount
        edges {
          node {
            ...likeItem
          }
        }
      }
      seen {
        totalCount
        edges {
          node {
            ...postSeenItem
          }
        }
      }
      state

      course {
        id
        title
      }

      students {
        totalCount
        edges {
          node {
            ...studentItem
          }
        }
      }

      isPrivate
      isHiddenFromStudents
      items {
        item {
          ... on StudentTemplateResponse {
            id
            metadata
            template {
              id
              fields {
                id
                label
                placeholder
                type
                metadata
                dataSets {
                  title
                  type
                  values {
                    value
                    date
                    displaySequence
                    id
                    item {
                      id
                      itemName
                    }
                  }
                }
              }
            }
            individuallyRated
            routineStudent: student {
              ...studentItem
            }
            fields {
              id
              value
              uid
              resolvedMinimalTree {
                id
                type
                ... on ResolvedFieldAttachmentGroup {
                  attachmentGroups {
                    id
                    displaySequence
                    metadata
                    attachments {
                      id
                      type
                      url
                      displaySequence
                    }
                  }
                }
              }
            }
            ...routineLikesAndComments
          }
        }
        itemType
      }
    }
    ${studentFragment.basicStudentDetails}
    ${attachmentFragment.attachment}
    ${userFragment.basicUserDetails}
    ${likefragment.like}
    ${postSeenFragment.postSeen}
    ${routineLikesAndComments.likesAndComments}
  `,
};

export const courseRoutinePostFragment = {
  courseRoutinePost: gql`
    fragment courseItem on Course {
      id
      posts(last: 5, after: $after, filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            ...postItem
          }
        }
      }
    }
    ${routinePostFragment.routinePostFeed}
  `,
};
